import { useEffect, useRef, useState } from 'react'
import { STAGE } from '@sdg/shared'
import { cn } from '../../lib/cn'
import { CountdownDial } from './CountdownDial'
import { celebrate } from '../Screen'
import { stageSound } from '../../lib/stageAudio'

/**
 * The count before an award place is revealed.
 *
 * The lucky draw has its wheel and its dial; the ceremony used to have
 * neither — Admin pressed "Reveal next" and a name simply appeared, which is
 * the one moment of the day that should not be quiet. This gives every reveal
 * the same beat: ten seconds of counting over the card that is about to
 * open, then the card lands with confetti.
 *
 * The count is per *place*, not per award, so revealing 3rd, 2nd and 1st in
 * turn counts three separate times — which is how an awards ceremony is run.
 */

/**
 * Ten seconds, at the Committee's request — long enough for the compère to
 * name the award and the place, and for the hall to quiet down, before the
 * card opens.
 */
const COUNT_MS = STAGE.revealCountdownMs

export interface RevealState {
  /** The identity currently counting down, or null when nothing is. */
  pending: string | null
  /** Counts down to 1 — only meaningful while `pending` is set. */
  count: number
}

/**
 * How recent a reveal has to be for a screen that has only just appeared to
 * count it down rather than adopt it.
 *
 * In "follow the event" mode the award screen is not on the projector until
 * something is revealed — so Admin's first "Reveal next" both *mounts* this
 * component and delivers the reveal in the same payload. The mount guard
 * below exists to stop a re-count on reconnect, and it was swallowing that
 * first announcement: the first attire winner appeared with no countdown and
 * only the second one counted.
 *
 * Judged on the reveal's own timestamp instead. The window is the length of
 * the countdown itself, so a browser clock a few seconds off a server one
 * still behaves: at worst a fresh reveal is adopted silently (what happened
 * before) rather than an old one being announced twice. Derived rather than
 * written down, so changing the countdown cannot leave this behind.
 */
const FRESH_MS = COUNT_MS

/**
 * How far in the *future* a timestamp may sit and still count as just now.
 *
 * MySQL rounds a `datetime` to the nearest second, so a reveal written at
 * x.6s reads back as x+1 — up to half a second ahead of the clock that wrote
 * it. Add any skew between the server and the hall's laptop and the age of a
 * reveal that happened a moment ago can be negative. Requiring `age >= 0`
 * therefore threw away the countdown at random, which is most of what made
 * this look intermittent.
 */
const CLOCK_SLACK_MS = 3000

/**
 * Counts down whenever `identity` changes to something this screen has not
 * shown before.
 *
 * `identity` must change exactly once per reveal — place plus the time it was
 * revealed — because the stream re-sends the whole ceremony on every payload
 * and on reconnect. Counting down again for a name the hall saw ten minutes
 * ago would read as a second announcement.
 */
export function useRevealCountdown(
  identity: string | null,
  theme: 'gold' | 'purple',
  /** When the newest reveal happened, ISO, so a just-mounted screen can tell. */
  revealedAt?: string | null,
  /**
   * Fired the moment the curtain lifts, with the confetti.
   *
   * This is where the award's sound belongs — not when the server reveals the
   * place, which is ten seconds earlier and behind a curtain. A reveal that
   * is *adopted* in silence (a screen that has only just appeared) does not
   * call it, for the same reason it gets no countdown.
   */
  onLand?: () => void,
): RevealState {
  /*
   * Held in a ref, not read from the closure: the callback is written inline
   * at the call site, so it is a new function on every render. In the effect's
   * dependencies it would restart the count ten times a second.
   */
  const onLandRef = useRef(onLand)
  onLandRef.current = onLand

  const [pending, setPending] = useState<string | null>(null)
  const [count, setCount] = useState(() => Math.ceil(COUNT_MS / 1000))
  const shown = useRef<string | null>(null)
  const mounted = useRef(false)

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true

      const age = revealedAt ? Date.now() - Date.parse(revealedAt) : Infinity

      // Anything already on screen before this component existed is adopted
      // in silence; a reveal that landed a moment ago still gets its count.
      if (!(age > -CLOCK_SLACK_MS && age < FRESH_MS)) {
        shown.current = identity
        return
      }
    }

    if (identity == null || identity === shown.current) return

    /*
     * `shown.current` is rolled back by the cleanup unless the count actually
     * finished — see the bottom of this effect. React's StrictMode runs every
     * effect twice on mount (run, clean up, run again), and without the
     * rollback the second run saw its own first run's bookkeeping, decided
     * the reveal had already been announced, and returned without starting a
     * timer. The curtain appeared, stuck on "6", and never lifted. Only the
     * *first* reveal was affected, because every later one is a real
     * dependency change rather than a mount.
     */
    const previous = shown.current
    shown.current = identity

    setPending(identity)
    setCount(Math.ceil(COUNT_MS / 1000))

    const started = Date.now()
    let finished = false

    /*
     * A timer rather than `requestAnimationFrame`: rAF is suspended in a
     * background tab, and the hall's laptop is not guaranteed to have this
     * window in front. A reveal that never completes would leave the dial
     * frozen at the audience for the rest of the ceremony.
     */
    const timer = window.setInterval(() => {
      const left = COUNT_MS - (Date.now() - started)

      if (left <= 0) {
        finished = true
        window.clearInterval(timer)
        setPending(null)
        onLandRef.current?.()
        celebrate(theme)
        return
      }

      setCount(Math.max(1, Math.ceil(left / 1000)))
    }, 100)

    return () => {
      window.clearInterval(timer)
      // Let a re-run start it again — StrictMode's second pass, or a remount
      // while the reveal is still fresh. A finished count is never replayed.
      if (!finished) shown.current = previous
    }
  }, [identity, theme])

  /*
   * The same tick the lucky draw uses, once per second of the curtain.
   *
   * Fired on the change of the displayed number rather than on the 100ms
   * timer, so it sounds ten times across a ten-second reveal and not a
   * hundred. The award ceremony and the draw now count in the same voice,
   * which is what tells the hall that something is about to be announced
   * whichever part of the programme it is in.
   */
  const ticked = useRef<number | null>(null)
  useEffect(() => {
    if (pending == null) {
      ticked.current = null
      return
    }
    if (ticked.current === count) return
    ticked.current = count
    stageSound.countdownTick(count)
  }, [pending, count])

  return { pending, count }
}

/**
 * What the hall looks at while the count runs: the award's name, the place
 * about to be announced, and the number.
 *
 * It covers the stage rather than sitting inside a card. The compère is
 * speaking over this — "and now, Best SDG Project, in third place…" — so the
 * screen should be showing that one thing and not a board of cards with a
 * small number ticking in a corner. The result underneath is already in the
 * payload but stays shut until the curtain lifts, so the screen and the room
 * find out together.
 */
export function RevealCurtain({
  title,
  place,
  count,
}: {
  title: string
  /** '3rd place', 'Male category', 'Winner' — whatever is being announced. */
  place: string
  count: number
}) {
  return (
    <div className="stage-enter absolute inset-0 z-50 grid place-items-center bg-[#0A0F24]/85 backdrop-blur-[0.5vmin]">
      <div className="flex flex-col items-center px-[4vmin] text-center">
        <p className="text-[1.9vmin] font-extrabold uppercase tracking-[0.42em] text-[#FFD16A]">
          Now revealing
        </p>

        <div className="mt-[1.4vmin] flex items-center gap-[1.6vmin]">
          <span className="h-px w-[7vmin] bg-[#E6A23C]/70" />
          <h2 className="max-w-[72vw] text-[5.6vmin] font-extrabold uppercase leading-none tracking-[0.1em] text-white drop-shadow-[0_4px_18px_rgba(5,10,35,0.6)]">
            {title}
          </h2>
          <span className="h-px w-[7vmin] bg-[#E6A23C]/70" />
        </div>

        <p className="mt-[1.6vmin] text-[2.9vmin] font-bold uppercase tracking-[0.2em] text-white/85">
          {place}
        </p>

        <CountdownDial
          seconds={count}
          total={Math.ceil(COUNT_MS / 1000)}
          size="30vmin"
          className="mt-[3vmin]"
        />
      </div>
    </div>
  )
}
